import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useConnectSource, useStartInstall, type ConnectSourceResult } from '../api.ts';

import type { SettingsOutletContext } from './Settings.tsx';

/**
 * /setup/link-repo — link a new repository without leaving the settings
 * shell. This replaces the old "Link New Repo" jump to the standalone
 * onboarding flow (which closed the menu and lost the user's place). The
 * provider choice mirrors onboarding's Connect step but stays in-shell and
 * reports success in place instead of redirecting.
 */
export function SettingsLinkRepo() {
  const { orgSlug } = useOutletContext<SettingsOutletContext>();
  const [mode, setMode] = useState<'choose' | 'gitlab'>('choose');

  return (
    <div>
      <h1 className="settings-h1">Link New Repo</h1>
      <p className="settings-subtitle">
        Connect a repository so we can ingest its history. We never push to, modify, or write to
        your repositories.
      </p>
      <div style={{ marginTop: 'var(--6u)' }}>
        {mode === 'choose' ? (
          <ChooseProvider orgSlug={orgSlug} onPickGitLab={() => setMode('gitlab')} />
        ) : (
          <GitLabConnect orgSlug={orgSlug} onBack={() => setMode('choose')} />
        )}
      </div>
    </div>
  );
}

function ChooseProvider({ orgSlug, onPickGitLab }: { orgSlug: string; onPickGitLab: () => void }) {
  const startInstall = useStartInstall(orgSlug);

  return (
    <div className="stack">
      <div className="row" style={{ gap: 'var(--3u)' }}>
        <button
          type="button"
          className="btn"
          disabled={startInstall.isPending}
          onClick={() =>
            startInstall.mutate(
              {},
              {
                onSuccess: ({ install_url }) => {
                  window.location.href = install_url;
                },
              },
            )
          }
        >
          {startInstall.isPending ? 'Opening GitHub…' : 'Connect GitHub'}
        </button>
        <button type="button" className="btn" onClick={onPickGitLab}>
          Connect GitLab or Azure
        </button>
      </div>
      {startInstall.isError ? (
        <p style={{ color: '#c0392b' }}>{startInstall.error.message}</p>
      ) : null}
    </div>
  );
}

function GitLabConnect({ orgSlug, onBack }: { orgSlug: string; onBack: () => void }) {
  const connect = useConnectSource(orgSlug);
  const [repoUrl, setRepoUrl] = useState('');
  const [token, setToken] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('');
  const [result, setResult] = useState<ConnectSourceResult | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!repoUrl || !token) return;
    connect.mutate(
      { repo_url: repoUrl, token, default_branch: defaultBranch.trim() || undefined },
      {
        onSuccess: (r) => {
          setResult(r);
          setToken('');
        },
      },
    );
  };

  return (
    <form onSubmit={submit} className="stack" style={{ maxWidth: '32rem' }}>
      <p className="muted">
        Paste a repository URL and an access token. <strong>GitLab</strong>: a token with the{' '}
        <code>read_repository</code> scope and at least the Reporter role. <strong>Azure</strong>: a
        PAT with Code (Read) scope. The token is stored encrypted and used only to clone for ingest.
      </p>
      <label className="stack" style={{ display: 'flex', flexDirection: 'column' }}>
        Repository URL
        <input
          type="text"
          placeholder="https://gitlab.com/group/project"
          value={repoUrl}
          onChange={(e) => setRepoUrl(e.target.value)}
          required
        />
      </label>
      <label className="stack" style={{ display: 'flex', flexDirection: 'column' }}>
        Access token
        <input
          type="password"
          placeholder="glpat-… or Azure PAT"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="off"
          required
        />
      </label>
      <label className="stack" style={{ display: 'flex', flexDirection: 'column' }}>
        <span>
          Default branch <span className="muted">(optional)</span>
        </span>
        <input
          type="text"
          placeholder="main"
          value={defaultBranch}
          onChange={(e) => setDefaultBranch(e.target.value)}
        />
      </label>
      <div className="row" style={{ gap: 'var(--3u)' }}>
        <button type="submit" className="btn" disabled={connect.isPending || !repoUrl || !token}>
          {connect.isPending ? 'Connecting…' : 'Connect repository'}
        </button>
        <button type="button" className="secondary-function" onClick={onBack}>
          Back
        </button>
      </div>
      {connect.isError ? <p style={{ color: '#c0392b' }}>{connect.error.message}</p> : null}
      {result ? (
        <p className="muted">
          Connected <code>{result.repository.prefix}</code> · backfill {result.backfill_status}.
        </p>
      ) : null}
    </form>
  );
}
